import React from "react";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../../requests/shared/helpers/DrawerFooterContainerLarge";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import ListSubheader from "@mui/material/ListSubheader";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import { formatName, PurpleSwitch } from "../../../../helpers/helpers";
import {
  AUTOCOMPLETE_TEXTFIELD_SX,
  CHECKBOX_SX,
  DrawerLabel,
  DrawerSaveFooter,
  INPUT_SX,
  SELECT_SX,
} from "./drawerInputs";

const SOURCE_GROUP_LABELS = {
  daily_stats: "Store data",
  reporting: "More store metrics",
  compliance: "Labor law violations",
  labor_split: "Labor by type",
  departments: "Labor by department",
  log_categories: "Numeric log categories",
};

// Settings for one metric card block: source, target, and what a flag
// demands. One boundary per metric (flagged or not), with an optional
// per-store target override.
function MetricBlockDrawer(props) {
  const [config, setConfig] = React.useState({});
  const [showPerStore, setShowPerStore] = React.useState(false);
  const [notifyEnabled, setNotifyEnabled] = React.useState(false);

  React.useEffect(() => {
    if (props.open) {
      const next = { ...(props.block?.config || {}) };
      setConfig(next);
      setShowPerStore(Object.keys(next.per_store_targets || {}).length > 0);
      setNotifyEnabled((next.notify_user_ids || []).length > 0);
    }
  }, [props.open, props.block]);

  const setField = (name, value) =>
    setConfig((prev) => ({ ...prev, [name]: value }));

  const sources = props.sources || [];
  const selectedSource = sources.find((s) => s.key === config.source);
  const hasThreshold =
    config.target !== null && config.target !== undefined && config.target !== "";
  const notifyUsers = (props.notifyOptions || []).filter((u) =>
    (config.notify_user_ids || []).includes(parseInt(u.id))
  );

  const groupedSources = sources.reduce((groups, source) => {
    (groups[source.group] = groups[source.group] || []).push(source);
    return groups;
  }, {});

  return (
    <Drawer anchor="right" open={props.open} onClose={props.onClose}>
      <div className="w-[450px] h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader title="Metric Card" onClose={props.onClose} />
          <div className="w-full px-6">
            <p className="text-slate-500 text-sm -mt-6">
              Source, target, and what a flag demands
            </p>

            <div className="mt-7">
              <DrawerLabel>Source</DrawerLabel>
              <div className="mt-1">
                <Select
                  fullWidth
                  size="small"
                  value={config.source || ""}
                  onChange={(e) => setField("source", e.target.value)}
                  sx={SELECT_SX}
                >
                  {Object.entries(groupedSources).flatMap(([group, groupSources]) => [
                    <ListSubheader key={`h-${group}`}>
                      {SOURCE_GROUP_LABELS[group] || group}
                    </ListSubheader>,
                    ...groupSources.map((source) => (
                      <MenuItem key={source.key} value={source.key}>
                        {source.label}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </div>
            </div>

            <div className="mt-7">
              <DrawerLabel>Label (Optional)</DrawerLabel>
              <div className="mt-1">
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={selectedSource?.label || "Metric name"}
                  value={config.label || ""}
                  onChange={(e) => setField("label", e.target.value || null)}
                  InputProps={{ sx: INPUT_SX }}
                />
              </div>
            </div>

            {config.source !== "weather" && (
              <>
                <div className="mt-7">
                  <DrawerLabel>Target (leave empty for no threshold)</DrawerLabel>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="number"
                      placeholder="e.g. 26"
                      value={config.target ?? ""}
                      onChange={(e) =>
                        setField(
                          "target",
                          e.target.value === "" ? null : e.target.value
                        )
                      }
                      InputProps={{ sx: INPUT_SX }}
                    />
                  </div>
                </div>

                {hasThreshold && (
                  <>
                    <div className="mt-7">
                      <DrawerLabel>Flag when</DrawerLabel>
                      <div className="mt-1">
                        <Select
                          fullWidth
                          size="small"
                          value={config.direction || "flag_below"}
                          onChange={(e) => setField("direction", e.target.value)}
                          sx={SELECT_SX}
                        >
                          <MenuItem value="flag_above">Above target</MenuItem>
                          <MenuItem value="flag_below">Below target</MenuItem>
                        </Select>
                      </div>
                    </div>

                    <div className="mt-7">
                      <div className="flex items-center justify-between">
                        <p className="text-slate-500 text-sm">
                          Require a manager response when flagged?
                        </p>
                        <div className="ml-4">
                          <PurpleSwitch
                            checked={Boolean(config.require_response)}
                            onChange={(e) =>
                              setField("require_response", e.target.checked)
                            }
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        The day's log shows an outstanding response until
                        someone explains the flag.
                      </p>
                    </div>

                    <div className="mt-7">
                      <div className="flex items-center justify-between">
                        <p className="text-slate-500 text-sm">
                          Do you want flags on this metric to notify specific
                          people?
                        </p>
                        <div className="ml-4">
                          <PurpleSwitch
                            checked={notifyEnabled}
                            onChange={(e) => {
                              setNotifyEnabled(e.target.checked);
                              if (!e.target.checked) {
                                setField("notify_user_ids", []);
                              }
                            }}
                          />
                        </div>
                      </div>
                      {notifyEnabled && (
                        <div className="mt-2">
                          <Autocomplete
                            multiple
                            fullWidth
                            disableClearable
                            disableCloseOnSelect
                            options={props.notifyOptions || []}
                            getOptionLabel={(option) => formatName(option.name)}
                            value={notifyUsers}
                            onChange={(event, newValue) =>
                              setField(
                                "notify_user_ids",
                                newValue.map((u) => parseInt(u.id))
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Select members to notify"
                                size="small"
                                sx={AUTOCOMPLETE_TEXTFIELD_SX}
                              />
                            )}
                            renderOption={(optionProps, option, { selected }) => (
                              <li {...optionProps} key={option.id}>
                                <Checkbox
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                  sx={CHECKBOX_SX}
                                />
                                {formatName(option.name)}
                              </li>
                            )}
                          />
                        </div>
                      )}
                    </div>

                    <div className="mt-7">
                      <div
                        className="text-sm text-violet-700 font-medium cursor-pointer"
                        onClick={() => setShowPerStore(!showPerStore)}
                      >
                        {showPerStore ? "Hide" : "Show"} per-store targets
                      </div>
                      {showPerStore && (
                        <div className="mt-2 flex flex-col gap-2">
                          {(props.storeNames || []).map((store) => (
                            <div key={store.id} className="flex items-center gap-2">
                              <p className="flex-1 text-sm text-slate-700 truncate">
                                {store.store_name}
                              </p>
                              <TextField
                                variant="outlined"
                                size="small"
                                type="number"
                                className="w-28"
                                placeholder="Default"
                                value={config.per_store_targets?.[String(store.id)] ?? ""}
                                onChange={(e) => {
                                  const next = { ...(config.per_store_targets || {}) };
                                  if (e.target.value === "") {
                                    delete next[String(store.id)];
                                  } else {
                                    next[String(store.id)] = e.target.value;
                                  }
                                  setField("per_store_targets", next);
                                }}
                                InputProps={{ sx: INPUT_SX }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

          </div>
          <DrawerFooterContainerLarge>
            <DrawerSaveFooter
              disabled={!config.source}
              onSave={() => {
                const next = { ...config };
                if (!hasThreshold) {
                  // The threshold UI is hidden without a company target, but
                  // resolve_target still applies retained store overrides, so
                  // clearing the target must clear them too or those stores
                  // keep flagging invisibly.
                  delete next.per_store_targets;
                }
                props.onSave(next);
                props.onClose();
              }}
              onClose={props.onClose}
            />
          </DrawerFooterContainerLarge>
          <div className="h-[200px]"></div>
        </div>
      </div>
    </Drawer>
  );
}

export default MetricBlockDrawer;
